import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_URL } from "../assets/constant";

export const SecuritySettingsModal = ({ showModal, onClose }) => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  const token = useSelector((state) => state.auth?.token);
  const userId = useSelector((state) => state.auth?.user?.id);

  // Fetch current 2FA status when modal opens
  useEffect(() => {
    if (showModal && userId) {
      fetchTwoFactorStatus();
    }
  }, [showModal, userId]);

  // GET: Fetch current 2FA status
  const fetchTwoFactorStatus = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      const response = await axios.post(
        `${BASE_URL}/users/two-factor-preference`,
        {
          user_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.success) {
        setTwoFactorEnabled(response.data.two_factor_enabled);
      } else {
        setError("Failed to load security settings");
      }
    } catch (error) {
      console.error("Error fetching 2FA status:", error);
      setError(
        error?.response?.data?.message || "Failed to load security settings"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // PUT: Update 2FA status
  const handleToggle2FA = async () => {
    const newStatus = !twoFactorEnabled;
    
    setIsSaving(true);
    setError("");
    setSuccessMessage("");
    
    try {
      const response = await axios.put(
        `${BASE_URL}/users/two-factor-preference`,
        {
          user_id: userId,
          two_factor_enabled: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.success) {
        setTwoFactorEnabled(response.data.two_factor_enabled);
        setSuccessMessage(
          response.data.message || 
          (newStatus 
            ? "Two-factor authentication enabled successfully" 
            : "Two-factor authentication disabled successfully")
        );
        
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Failed to update security settings");
      }
    } catch (error) {
      console.error("Error toggling 2FA:", error);
      setError(
        error?.response?.data?.message || "Failed to update security settings"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal 
      show={showModal} 
      onHide={onClose} 
      centered 
      size="md"
      className="security-modal"
      backdrop="static"
    >
      <Modal.Header className="security-modal-header">
        <div className="security-modal-icon">
          <i className="fa-solid fa-shield-halved"></i>
        </div>
        <Modal.Title className="security-modal-title">
          Security Settings
        </Modal.Title>
        <button type="button" className="security-close-btn" onClick={onClose}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </Modal.Header>
      
      <Modal.Body className="security-modal-body">
        {/* Success Message */}
        {successMessage && (
          <div className="security-success-message">
            <i className="fa-solid fa-circle-check"></i>
            <span>{successMessage}</span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="security-error-message">
            <i className="fa-solid fa-circle-exclamation"></i>
            <span>{error}</span>
          </div>
        )}
        
        {isLoading ? (
          <div className="security-loading">
            <Spinner animation="border" variant="primary" />
            <p>Loading security settings...</p>
          </div>
        ) : (
          <>
            {/* 2FA Card */}
            <div className="security-card">
              <div className="security-card-left">
                <div className="security-icon-wrapper">
                  <i class="fa-solid fa-shield-halved"></i>
                </div>
                <div className="security-card-info">
                  <h4>Two-Factor Authentication</h4>
                  <p>Secure your account with 2FA</p>
                  <div className="security-status">
                    {twoFactorEnabled ? (
                      <>
                        <span className="security-status-dot enabled"></span>
                        <span>Enabled</span>
                      </>
                    ) : (
                      <>
                        <span className="security-status-dot disabled"></span>
                        <span>Disabled</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="security-card-right">
                <label className="security-switch">
                  <input
                    type="checkbox"
                    checked={twoFactorEnabled}
                    onChange={handleToggle2FA}
                    disabled={isSaving}
                  />
                  <span className="security-slider"></span>
                </label>
              </div>
            </div>
            
            {isSaving && (
              <div className="security-updating">
                <Spinner animation="border" size="sm" />
                <span>Updating...</span>
              </div>
            )}
            
            {/* Info Section */}
            <div className="security-info-section">
              <div className="security-info-icon">
                <i className="fa-regular fa-lightbulb"></i>
              </div>
              <div className="security-info-content">
                <h5>About Two-Factor Authentication</h5>
                <p>
                  Protect your account with an additional verification step during sign in.
                </p>
                {twoFactorEnabled && (
                  <div className="security-info-note">
                    <i className="fa-solid fa-info-circle"></i>
                    <span>Your next login will require OTP verification</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer className="security-modal-footer">
        <Button className="security-cancel-btn" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};